import { AnimatePresence, motion } from 'framer-motion';
import { X, Share2, Bell, BellRing, Coffee, BarChart2, Sparkles, Users, ChevronRight, ArrowRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AnswerListModal } from './AnswerListModal';
import { FullStatsPage } from './FullStatsPage';
import { getMedalLedger } from '../lib/medals';
import { getLocalProfile } from '../lib/localProfile';
import { fetchTopicStats, TopicStatRow } from '../lib/communityStats';
import {
  buildCommunityView,
  buildScoreBoard,
  missFlavor,
  officialRanks,
  pctLabel,
  pickVerdict,
  rarestJoke,
  type FinishedGameRow,
  type GuessLike,
  type AnswerLike,
  type ScoreBoard,
} from '../lib/communityView';
import { formatLocalYmd } from '../lib/dates';
import { archivePath } from '../lib/archivePaths';
import { SITE_ORIGIN } from '../lib/seo';
import { rankAccent, RANK_SPECTRUM } from '../lib/rankColors';
import { medalCount, personalHeadline } from '../lib/personalStats';

function formatCountdown(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function StatsModal({
  userId,
  topicId,
  topicTitle,
  topicPlayDate,
  topicSources,
  arcadeLocked,
  autoOpenFullList,
  onStartArcadeMode,
  onRevealAllAnswers,
  score,
  guesses,
  allAnswers,
  onClose
}: {
  userId: string;
  topicId: string;
  topicTitle: string;
  topicPlayDate: string;
  topicSources?: {
    source_1?: string | null;
    source_2?: string | null;
    source_3?: string | null;
  };
  arcadeLocked?: boolean;
  autoOpenFullList?: boolean;
  onStartArcadeMode?: () => void;
  onRevealAllAnswers?: () => void;
  score: number;
  guesses: GuessLike[];
  allAnswers?: AnswerLike[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [scoreBoard, setScoreBoard] = useState<ScoreBoard | null>(null);
  const [profile, setProfile] = useState<{ games_played: number; current_streak: number; max_streak: number; high_score: number } | null>(null);
  const [medals, setMedals] = useState(() => getMedalLedger(userId));
  const [nextTopicCountdown, setNextTopicCountdown] = useState<string>('00:00:00');
  const [showFullList, setShowFullList] = useState(false);
  const [showFullStats, setShowFullStats] = useState(false);
  const [fullListLoading, setFullListLoading] = useState(false);
  const [fullList, setFullList] = useState<{ id: string; name: string; rank: number }[]>([]);
  const [reminderStatus, setReminderStatus] = useState<NotificationPermission | 'idle'>('idle');
  const [communityRows, setCommunityRows] = useState<TopicStatRow[] | null>(null);

  useEffect(() => {
    if (guesses.length < 5) return;
    let cancelled = false;
    fetchTopicStats(topicId).then((rows) => {
      if (!cancelled) setCommunityRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [topicId, guesses.length]);

  const community = useMemo(
    () => (communityRows ? buildCommunityView(communityRows, allAnswers ?? [], topicId) : null),
    [communityRows, allAnswers, topicId]
  );

  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setReminderStatus('granted');
      } else if (Notification.permission === 'denied') {
        setReminderStatus('denied');
      }
    }
  }, []);

  const handleRemindMe = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      alert('Notifications are not supported in your browser.');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setReminderStatus(permission);

      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        
        // Calculate next midnight
        const now = new Date();
        const nextMidnight = new Date(now);
        nextMidnight.setHours(24, 0, 0, 0);

        // Try to use the experimental Notification Triggers API if available
        if ('showTrigger' in Notification.prototype) {
          await registration.showNotification("Time to play Centumth!", {
            body: "Today's puzzle is ready.",
            icon: "/logo192.png",
            // @ts-ignore - experimental API
            showTrigger: new (window as any).TimestampTrigger(nextMidnight.getTime())
          });
          alert('Reminder set! You will be notified when the next puzzle is ready.');
        } else {
          // Fallback for browsers without showTrigger
          alert('Notifications enabled! (Note: Background notifications require a push server on iOS/Safari, but we will remind you if you keep the tab open).');
          
          const timeUntilMidnight = nextMidnight.getTime() - now.getTime();
          setTimeout(() => {
            registration.showNotification("Time to play Centumth!", {
              body: "Today's puzzle is ready.",
              icon: "/logo192.png"
            });
          }, timeUntilMidnight);
        }
      }
    } catch (error) {
      console.error('Error setting up notification:', error);
    }
  };

  // Calculate actual score (excluding hints)
  const officialGuesses = guesses.slice(0, 5);
  const actualScore = officialGuesses.reduce((acc, curr) => acc + (curr.isHint ? 0 : (curr.rank || 0)), 0);
  const yourRankList = officialRanks(officialGuesses);
  const yourRanks = new Set(yourRankList);
  const listRevealed = !!arcadeLocked;
  const verdict = community
    ? pickVerdict({
        topicId,
        userId,
        score: actualScore,
        guesses: officialGuesses,
        yourRanks: yourRankList,
        community,
        median: scoreBoard?.median ?? null,
        percentile: scoreBoard?.percentile ?? null,
      })
    : null;

  useEffect(() => {
    // Same round-trip as before — just also grab the five ranks for the nerd page.
    async function loadStats() {
      const { data, error } = await supabase
        .from('guesses')
        .select('score, guess_1_rank, guess_2_rank, guess_3_rank, guess_4_rank, guess_5_rank')
        .eq('topic_id', topicId)
        .eq('completed', true);

      const realRows: FinishedGameRow[] = !error && data
        ? data.map((g: any) => ({
            score: Number(g.score ?? 0),
            ranks: [g.guess_1_rank, g.guess_2_rank, g.guess_3_rank, g.guess_4_rank, g.guess_5_rank].map((r: unknown) => {
              const n = Number(r);
              return Number.isFinite(n) && n > 0 ? n : null;
            }),
          }))
        : [];

      setScoreBoard(buildScoreBoard(realRows, topicId, actualScore));
    }
    loadStats();
  }, [topicId, actualScore]);

  useEffect(() => {
    const p = getLocalProfile(userId);
    setProfile({
      games_played: p.games_played ?? 0,
      current_streak: p.current_streak ?? 0,
      max_streak: p.max_streak ?? 0,
      high_score: p.high_score ?? 0,
    });
  }, [userId]);

  useEffect(() => {
    // Refresh medal ledger when opening the modal or switching topics.
    setMedals(getMedalLedger(userId));
  }, [userId, topicId]);

  // Percent bars: 0..100

  const getMedal = (s: number) => {
    if (s >= 490) return { id: 'platinum', icon: '💎', name: 'Platinum', color: 'text-cyan-400' };
    if (s >= 300) return { id: 'gold', icon: '🥇', name: 'Gold', color: 'text-yellow-400' };
    if (s >= 150) return { id: 'silver', icon: '🥈', name: 'Silver', color: 'text-gray-400' };
    if (s >= 50) return { id: 'bronze', icon: '🥉', name: 'Bronze', color: 'text-orange-400' };
    return null;
  };

  const generateEmojiGrid = () => {
    const parts = [];
    for (let i = 0; i < 5; i++) {
      const guess = guesses[i];
      if (!guess) {
        parts.push('⬜');
      } else if (guess.isHint) {
        parts.push('💡');
      } else if (!guess.rank || guess.valid === false) {
        parts.push('❌');
      } else {
        const r = guess.rank;
        let emoji = '';
        if (r > 90) emoji = '🎯';
        else if (r > 80) emoji = '🔵';
        else if (r > 50) emoji = '🟢';
        else if (r > 20) emoji = '🟡';
        else emoji = '🟠';
        parts.push(`${emoji} ${r}`);
      }
    }
    return parts.join(' | ');
  };

  const emojiGrid = generateEmojiGrid();

  const medal = getMedal(actualScore);

  useEffect(() => {
    const tick = () => {
      // Wordle-style: next topic changes at the user's next local midnight.
      const now = new Date();
      const nextLocalMidnight = new Date(now);
      nextLocalMidnight.setHours(24, 0, 0, 0);
      const ms = nextLocalMidnight.getTime() - now.getTime();
      setNextTopicCountdown(formatCountdown(ms));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  const openFullList = async () => {
    setShowFullList(true);
    if (fullList.length > 0) return;
    setFullListLoading(true);
    const { data } = await supabase
      .from('answers')
      .select('id,name,rank')
      .eq('topic_id', topicId)
      .order('rank', { ascending: true });
    setFullList((data as any[])?.map((r) => ({ id: r.id, name: r.name, rank: Number(r.rank) })) ?? []);
    setFullListLoading(false);
  };

  const revealAllAnswers = async () => {
    onRevealAllAnswers?.();
    await openFullList();
  };

  const shareResults = () => {
    const medalIcon = medal?.icon ?? '';
    
    let formattedDate = topicPlayDate;
    if (topicPlayDate && topicPlayDate.includes('-')) {
      const [y, m, d] = topicPlayDate.split('-');
      const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
      formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    // Archive shares deep-link to that puzzle; today's game shares the homepage.
    const playDate = (topicPlayDate || '').slice(0, 10);
    const shareUrl =
      playDate && playDate < formatLocalYmd()
        ? `${SITE_ORIGIN}${archivePath({ title: topicTitle, play_date: playDate })}`
        : SITE_ORIGIN;

    const text = `Centumth ${formattedDate} - ${topicTitle}\n${emojiGrid}\nTotal: ${actualScore}/490${medalIcon ? ' ' + medalIcon : ''}\n\n${shareUrl}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isGameFinished = guesses.length >= 5;

  useEffect(() => {
    if (!autoOpenFullList) return;
    if (!isGameFinished) return;
    void revealAllAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenFullList, isGameFinished]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-background border-2 border-border rounded-3xl shadow-2xl relative max-h-[85vh] overflow-hidden"
      >
        {/* Sticky top bar so close is always reachable */}
        <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border px-6 py-4 flex items-center justify-center">
          <div className="text-sm font-semibold text-foreground/50 uppercase tracking-widest">Results</div>
          <button
            onClick={onClose}
            className="absolute right-4 top-3 p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(85vh-56px)]">

        {!isGameFinished ? (
          <div className="flex flex-col items-center mb-10 text-center">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
              <BarChart2 size={32} className="text-foreground/50" />
            </div>
            <h3 className="text-xl font-bold mb-2">Keep Playing!</h3>
            <p className="text-foreground/60 text-sm">
              Finish your 5 guesses to unlock community stats, the full top 100 list, and shareable results for this puzzle.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center mb-8">
            {medal && (
              <motion.div 
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                className={`text-5xl mb-1 drop-shadow-xl ${medal.color}`}
                title={medal.name}
              >
                {medal.icon}
              </motion.div>
            )}
            <div className="text-sm font-semibold text-foreground uppercase tracking-widest mb-1">Total Score</div>
            <div className="text-6xl font-black tracking-tighter tabular-nums">
              {actualScore}<span className="text-2xl text-foreground">/490</span>
            </div>
            <div className="mt-3 text-xs font-semibold text-foreground/60 uppercase tracking-widest">
              Next topic in <span className="text-foreground">{nextTopicCountdown}</span>
            </div>
            {scoreBoard && (
              <div className="mt-3 text-sm font-medium text-foreground/70 bg-muted px-4 py-1.5 rounded-full">
                You scored better than <span className="font-bold text-foreground">{scoreBoard.percentile}%</span> of players
              </div>
            )}

            <button
              onClick={shareResults}
              className="w-full mt-5 bg-foreground text-background py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-1"
            >
              <div className="flex items-center gap-2">
                {copied ? 'Copied!' : 'Share'} <Share2 size={20} />
              </div>
              <div className="text-sm font-normal tracking-widest opacity-80">
                {emojiGrid}
              </div>
            </button>
            <a
              href="https://www.majoritygame.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-full mt-3 flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-3.5 py-3 hover:bg-muted hover:border-foreground/20 transition-all active:scale-[0.98]"
            >
              <img
                src="/majority-favicon.png"
                alt=""
                className="h-10 w-10 rounded-xl shrink-0 bg-white"
              />
              <span className="min-w-0 text-left flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-foreground/45">
                  Try out our new game
                </span>
                <span className="block text-base font-black leading-tight">Majority</span>
              </span>
              <ArrowRight
                size={16}
                className="shrink-0 text-foreground/40 transition-transform group-hover:translate-x-0.5"
              />
            </a>
            <a
              href="https://www.trivmount.com/home"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-full mt-3 flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-3.5 py-3 hover:bg-muted hover:border-foreground/20 transition-all active:scale-[0.98]"
            >
              <img
                src="/trivmount-favicon.png"
                alt=""
                className="h-10 w-10 rounded-xl shrink-0"
              />
              <span className="min-w-0 text-left flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-foreground/45">
                  Try out our other game
                </span>
                <span className="block text-base font-black leading-tight">Trivmount</span>
              </span>
              <ArrowRight
                size={16}
                className="shrink-0 text-foreground/40 transition-transform group-hover:translate-x-0.5"
              />
            </a>
            <a
              href="https://www.guessmyart.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-full mt-3 flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-3.5 py-3 hover:bg-muted hover:border-foreground/20 transition-all active:scale-[0.98]"
            >
              <img
                src="/guessmyart-logo.png"
                alt=""
                className="h-10 w-10 rounded-xl shrink-0"
              />
              <span className="min-w-0 text-left flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-foreground/45">
                  Try out our other game
                </span>
                <span className="block text-base font-black leading-tight">GuessMyArt</span>
              </span>
              <ArrowRight
                size={16}
                className="shrink-0 text-foreground/40 transition-transform group-hover:translate-x-0.5"
              />
            </a>
          </div>
        )}

        {isGameFinished && community && verdict && (
          <div className="mb-8">
            <div className="text-sm font-semibold text-foreground uppercase tracking-widest mb-4 flex items-center justify-center gap-2">
              <Users size={14} /> Community
            </div>

            <div className="text-center mb-5 px-1">
              <h3 className="text-[1.65rem] leading-tight font-black tracking-tight">{verdict.headline}</h3>
              <p className="mt-2 text-sm text-foreground/55 leading-relaxed">{verdict.sub}</p>
            </div>

            {(() => {
              const nameByRank = new Map((allAnswers ?? []).map((a) => [a.rank, a.name] as const));
              const maxCount = Math.max(1, ...Array.from(community.countByRank.values()));
              return (
                <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3 mb-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-3">
                    Guess distribution
                  </div>
                  <div className="flex items-end gap-px h-20">
                    {Array.from({ length: 100 }).map((_, i) => {
                      const rank = i + 1;
                      const count = community.countByRank.get(rank) ?? 0;
                      const isYours = yourRanks.has(rank);
                      const barHeight = Math.max(6, Math.round((count / maxCount) * 100));
                      const name = nameByRank.get(rank);
                      const showName = (listRevealed || isYours) && name;
                      const tooltipPos =
                        rank <= 10
                          ? 'left-0'
                          : rank >= 91
                            ? 'right-0'
                            : 'left-1/2 -translate-x-1/2';
                      return (
                        <div key={rank} className="relative group flex-1 h-full flex items-end cursor-default">
                          <div
                            className={`w-full rounded-full transition-opacity duration-200 ${
                              isYours ? 'opacity-100 ring-1 ring-foreground/80' : 'opacity-55 group-hover:opacity-90'
                            }`}
                            style={{ height: `${barHeight}%`, backgroundColor: rankAccent(rank) }}
                          />
                          <div
                            className={`pointer-events-none absolute bottom-full mb-2 hidden group-hover:block z-20 ${tooltipPos}`}
                          >
                            <div className="rounded-lg bg-foreground text-background text-[11px] font-bold px-2.5 py-1.5 whitespace-nowrap max-w-[240px] truncate shadow-lg">
                              #{rank}
                              {showName ? ` · ${name}` : ''}
                              {` · ${pctLabel(count, community.totalPlayers)}`}
                              {isYours ? ' · You' : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2.5 h-1 rounded-full" style={{ backgroundImage: RANK_SPECTRUM }} />
                  <div className="flex items-center justify-between mt-1.5 text-[9px] font-bold tracking-widest text-foreground/30 tabular-nums">
                    <span>#1</span>
                    <span className="text-[10px] font-semibold tracking-normal text-foreground/45">Your answers glow</span>
                    <span>#100</span>
                  </div>
                </div>
              );
            })()}

            {community.mostGuessed.length > 0 && (
              <div className="rounded-2xl border border-border bg-muted/40 px-3 pt-3 pb-3 mb-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 text-center">
                  Today’s podium
                </div>
                <div className="text-[11px] text-foreground/45 text-center mt-1 mb-3 leading-snug">
                  The three answers players guessed most today
                </div>
                <div className="grid grid-cols-3 gap-2 items-end">
                  {([1, 0, 2] as const).map((place) => {
                    const m = community.mostGuessed[place];
                    if (!m) return <div key={place} />;
                    const yours = yourRanks.has(m.rank);
                    const isFirst = place === 0;
                    return (
                      <div
                        key={m.rank}
                        className={`rounded-2xl text-center px-2 ${
                          isFirst ? 'bg-background border border-border py-3.5' : 'py-2.5'
                        }`}
                      >
                        <div className={`font-black tabular-nums ${isFirst ? 'text-lg' : 'text-sm text-foreground/45'}`}>
                          {place === 0 ? '1st' : place === 1 ? '2nd' : '3rd'}
                        </div>
                        <div
                          className="mx-auto mt-1.5 h-1.5 w-8 rounded-full"
                          style={{ backgroundColor: rankAccent(m.rank) }}
                        />
                        <div className={`mt-2 font-mono text-[11px] font-bold ${isFirst ? 'text-foreground/50' : 'text-foreground/35'}`}>
                          #{m.rank}
                        </div>
                        <div className={`mt-0.5 font-bold leading-snug break-words ${isFirst ? 'text-sm' : 'text-xs'}`}>
                          {m.name}
                        </div>
                        <div className="mt-1.5 text-[11px] font-black tabular-nums text-foreground/50">{m.label}</div>
                        {yours && (
                          <div className="mt-0.5 text-[9px] font-black uppercase tracking-widest text-foreground/40">You too</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {community.rarestFinds[0] && (
              <div className="rounded-2xl border border-dashed border-foreground/20 bg-muted/30 px-4 py-3.5 mb-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">
                  Rarest find
                </div>
                <div className="text-[11px] text-foreground/45 mt-0.5 mb-1.5">
                  The answer the fewest players got
                </div>
                <div className="text-sm font-semibold">
                  <span className="text-foreground/40 font-mono mr-2">#{community.rarestFinds[0].rank}</span>
                  {community.rarestFinds[0].name}
                  {yourRanks.has(community.rarestFinds[0].rank) && (
                    <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-foreground/40">You found it</span>
                  )}
                </div>
                <div className="text-xs text-foreground/55 mt-1.5 leading-relaxed italic">
                  {rarestJoke(topicId)} Only {community.rarestFinds[0].label} of players got there.
                </div>
              </div>
            )}

            {community.topMisses.length > 0 && (
              <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3.5 mb-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">
                  Popular misses
                </div>
                <div className="text-[11px] text-foreground/45 mt-0.5 mb-2">
                  Guesses a lot of people tried that aren’t on the list
                </div>
                <div className="flex flex-wrap gap-2">
                  {community.topMisses.slice(0, 3).map((m) => (
                    <div
                      key={m.text}
                      className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5"
                    >
                      <span className="text-sm font-semibold line-through decoration-foreground/30">{m.text}</span>
                      <span className="text-[10px] font-bold text-foreground/40">{m.label}</span>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-foreground/45 mt-2 italic">
                  {missFlavor(topicId).tags[0]}. The list said no.
                </div>
              </div>
            )}

            {scoreBoard && (
              <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3.5 mb-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">
                  Medal mix
                </div>
                <div className="text-[11px] text-foreground/45 mt-0.5 mb-3">
                  How today’s scores broke down
                </div>
                <div className="space-y-2">
                  {scoreBoard.medalMix.filter((m) => m.tier !== 'none' || m.pct > 0).map((m) => {
                    const yours =
                      (medal && m.tier === medal.id) ||
                      (!medal && m.tier === 'none');
                    return (
                      <div key={m.tier} className="flex items-center gap-3">
                        <div className="w-[4.75rem] shrink-0 text-xs font-bold text-foreground/60">
                          {m.icon} {m.label}
                        </div>
                        <div className="flex-1 h-2 bg-background rounded-full overflow-hidden border border-border">
                          <div
                            className={`h-full rounded-full ${yours ? '' : 'bg-foreground/40'}`}
                            style={{
                              width: `${Math.max(m.pct > 0 ? 4 : 0, Math.min(100, m.pct))}%`,
                              backgroundImage: yours
                                ? 'linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)'
                                : undefined,
                            }}
                          />
                        </div>
                        <div className="w-10 text-right text-xs font-bold tabular-nums text-foreground/50">
                          {m.pct}%
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-1.5 mt-3 text-[10px] font-semibold text-foreground/45">
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundImage: 'linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)' }}
                  />
                  You
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowFullStats(true)}
              className="w-full mt-1 border border-border rounded-xl py-3.5 font-semibold hover:bg-muted transition-colors flex items-center justify-center gap-1.5"
            >
              Show full stats <ChevronRight size={18} />
            </button>
          </div>
        )}

        {isGameFinished && (
          <div className="mb-8">
            <div className="text-sm font-semibold text-foreground uppercase tracking-widest mb-4 text-center">Keep Playing</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  if (arcadeLocked) return;
                  onStartArcadeMode?.();
                  onClose();
                }}
                disabled={!!arcadeLocked}
                className={`rounded-2xl px-3 py-3.5 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-0.5 text-center ${
                  arcadeLocked
                    ? 'bg-muted text-foreground/40 cursor-not-allowed'
                    : 'bg-foreground text-background hover:opacity-90'
                }`}
              >
                <div className="font-bold">Arcade Mode</div>
                <div className={`text-[11px] font-medium leading-tight ${arcadeLocked ? 'text-foreground/30' : 'text-background/70'}`}>
                  {arcadeLocked ? 'Locked — you revealed the list' : 'Keep guessing, score stays locked'}
                </div>
              </button>

              <button
                onClick={revealAllAnswers}
                className="rounded-2xl border border-border px-3 py-3.5 hover:bg-muted transition-colors flex flex-col items-center justify-center gap-0.5 text-center"
              >
                <div className="font-bold">Reveal All 100</div>
                <div className="text-[11px] font-medium leading-tight text-foreground/50">
                  Locks Arcade Mode
                </div>
              </button>
            </div>
          </div>
        )}

        {profile && (
          <div className="mb-8">
            <div className="text-sm font-semibold text-foreground uppercase tracking-widest mb-4 text-center">You</div>
            {(() => {
              const you = personalHeadline(profile, medals);
              const hero =
                profile.current_streak > 0
                  ? { value: String(profile.current_streak), label: 'day streak' }
                  : { value: String(profile.high_score), label: 'personal best' };
              return (
                <div className="rounded-2xl border border-border bg-muted/40 px-4 py-4">
                  <div className="text-center">
                    <div className="text-5xl font-black tabular-nums tracking-tight">{hero.value}</div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/40 mt-1">
                      {hero.label}
                    </div>
                    <p className="mt-2.5 text-sm font-semibold leading-snug">{you.headline}</p>
                    <p className="mt-1 text-xs text-foreground/50 leading-relaxed">{you.sub}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <div className="rounded-xl bg-background border border-border px-3 py-2 text-center">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">Games</div>
                      <div className="text-lg font-black tabular-nums">{profile.games_played}</div>
                    </div>
                    <div className="rounded-xl bg-background border border-border px-3 py-2 text-center">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">Best</div>
                      <div className="text-lg font-black tabular-nums">{profile.high_score}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-3 mt-4 text-sm font-bold tabular-nums">
                    <span>🥉 {medals.bronze}</span>
                    <span>🥈 {medals.silver}</span>
                    <span>🥇 {medals.gold}</span>
                    <span>💎 {medals.platinum}</span>
                  </div>
                  {medalCount(medals) === 0 && (
                    <div className="text-center text-[11px] text-foreground/40 mt-1">No medals yet</div>
                  )}
                </div>
              );
            })()}
            <button
              type="button"
              onClick={() => navigate('/you')}
              className="w-full mt-3 border border-border rounded-xl py-3.5 font-semibold hover:bg-muted transition-colors flex items-center justify-center gap-1.5"
            >
              Your stats <ChevronRight size={18} />
            </button>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <a
            href="https://ko-fi.com/eliass_al"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)] text-black py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            Support the Creator <Coffee size={20} />
          </a>

          <div className="flex gap-3">
            <a
              href="https://tally.so/r/dWvW5V"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 border border-border py-4 rounded-xl font-semibold hover:bg-muted transition-colors flex items-center justify-center gap-2"
            >
              Suggest <Sparkles size={18} />
            </a>

            <button 
              onClick={handleRemindMe}
              disabled={reminderStatus === 'granted'}
              className={`flex-1 border-2 py-4 rounded-xl font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${
                reminderStatus === 'granted' 
                  ? 'border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400 cursor-default' 
                  : 'border-border hover:bg-muted text-foreground'
              }`}
            >
              {reminderStatus === 'granted' ? (
                <>
                  Reminder Set <BellRing size={18} />
                </>
              ) : (
                <>
                  Remind Me <Bell size={18} />
                </>
              )}
            </button>
          </div>
        </div>

        </div>
      </motion.div>

      {showFullList && (
        <AnswerListModal
          title={topicTitle}
          topicSources={topicSources}
          answers={fullList}
          loading={fullListLoading}
          highlightRanks={guesses.map((g) => (g.rank ? Number(g.rank) : -1)).filter((n) => n > 0)}
          onClose={() => setShowFullList(false)}
        />
      )}

      <AnimatePresence>
        {showFullStats && community && (
          <FullStatsPage
            topicTitle={topicTitle}
            topicPlayDate={topicPlayDate}
            score={actualScore}
            guesses={officialGuesses}
            community={community}
            scoreBoard={scoreBoard}
            onClose={() => setShowFullStats(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


